
import { useState, useEffect } from 'react';
import { useIsMobile } from './use-mobile';
import { logger } from '@/utils/logger';
import { toError } from '@/utils/toError';

export const useFirstLaunch = () => {
  const [isFirstLaunch, setIsFirstLaunch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const checkFirstLaunch = () => {
      try {
        const hasVisited = typeof window !== 'undefined' && window.localStorage ? 
          localStorage.getItem('app-visited') : null;
        const isFirstTime = !hasVisited;
        
        if (isFirstTime && typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('app-visited', 'true');
          localStorage.setItem('first-visit-date', new Date().toISOString());
        }
        
        logger.debug('First Launch Check:', { hasVisited, isFirstTime, isMobile });
        
        setIsFirstLaunch(isFirstTime && isMobile);
      } catch (err) {
        logger.error('Error checking first launch:', toError(err).message);
        setIsFirstLaunch(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (isMobile !== undefined) {
      checkFirstLaunch();
    }
  }, [isMobile]);

  const markAsReturningUser = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('returning-user', 'true');
      }
      setIsFirstLaunch(false);
    } catch (err) {
      logger.error('Error marking as returning user:', toError(err).message);
    }
  };

  const isPWAInstalled = () => {
    try {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as any).standalone === true;
      const hasPWAParam = window.location.search.includes('pwa=true');
      const isMarkedInstalled = typeof window !== 'undefined' && window.localStorage ? 
        localStorage.getItem('pwa-installed') === 'true' : false;
      
      const result = isStandalone || isIOSStandalone || hasPWAParam || isMarkedInstalled;
      
      logger.debug('PWA Install Check:', { isStandalone, isIOSStandalone, hasPWAParam, isMarkedInstalled, result });
      
      return result;
    } catch (err) {
      logger.error('Error checking PWA installation:', toError(err).message);
      return false;
    }
  };

  return {
    isFirstLaunch,
    isLoading,
    isMobile,
    markAsReturningUser,
    isPWAInstalled
  };
};
